import React, { Fragment, useContext, useState } from 'react'
import { CartsAppContext } from '../context/carts-app-context'
import { sumCartItems } from '../helpers/logic'
import OrderActions from './order-actions'
import PromoCode from './promo-code'
import { safeApiCall } from '../../services/api'

function OrderSummary({
  cartSubtotal,
  cartGCTotal,
  cartTransFee,
  cartPromoDiscount,
  cartPromoCode,
  cartRoundUp,
  cartTotal,
  cartMessage,
  cartExchangeCredit,
  cartExchangeFee,
  cartExchangeDetails,
  cartHasExchange,
  showPromoBox
}) {
  const { cart } = useContext(CartsAppContext)
  /*
  NOTE this is a different formatter than the one
  in the helpers file.  This one does NOT strip out .00
   */
  let USDollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  let { items, transactionFee, roundUp, exchangeFee, exchangeDetails } = cart

  const exchangeCredit = parseFloat(
    exchangeDetails?.exchangeAmount || cartExchangeCredit || 0
  )
  let computedTotal = sumCartItems(items) + exchangeCredit
  const subTotal = sumCartItems(items)
  const [promoDiscount, setPromoDiscount] = useState(cartPromoDiscount)
  const [promoCode, setPromoCode] = useState(cartPromoCode)
  const [newTotal, setNewTotal] = useState(cartTotal)
  const [message, setMessage] = useState(cartMessage)
  const [roundUpAmount, setRoundUpAmount] = useState(cartRoundUp || roundUp)
  const isAnExchange = cart.exchange === true || cartHasExchange === true
  const exchangeId = isAnExchange
    ? exchangeDetails
      ? exchangeDetails.exchangeId
      : cartExchangeDetails.id
    : null
  const exchangeOriginalOrder = isAnExchange
    ? exchangeDetails
      ? exchangeDetails.relatedOrder
      : cartExchangeDetails.original_order_external_id
    : null
  // If this is an exchange, then the subTotal is always the sum of the
  // items in the cart - the exchange credit.  Since users never get money back
  // if the value is < 0, it's just 0.
  if (isAnExchange) {
    if (computedTotal < 0) {
      computedTotal = 0
    }
  }
  const unroundedTotal = computedTotal + transactionFee + exchangeFee
  if (roundUp && (unroundedTotal + roundUp) % 1 !== 0) {
    roundUp = Math.ceil(unroundedTotal) - unroundedTotal
  }

  const total = newTotal
    ? (parseFloat(newTotal) + parseFloat(cartGCTotal)).toString()
    : computedTotal + transactionFee + exchangeFee + roundUp
  const announcement = `Cart total is ${USDollarFormatter.format(total)}`
  const abortExchange = async () => {
    const exchangeUrl = `/exchanges/${exchangeId}`

    await safeApiCall('delete', exchangeUrl, 'Exchange could not be aborted')

    // Regardless of whether or not this passed, the only thing left to do is go to the original order.
    window.location.href = `/order_history/${exchangeOriginalOrder}`
  }

  return (
    <div
      data-testid="order-summary-sidebar"
      className="bg-white md:mt-0 md:shadow md:p-24"
    >
      <div className="flex justify-between items-center">
        <h2>Order Summary</h2>
        {!cart.id && (
          <a
            className="text-link"
            href="/cart"
          >
            Edit
          </a>
        )}
      </div>
      <span
        className="sr-only"
        role="status"
      >
        {announcement}
      </span>

      <dl
        className="flex flex-wrap text-16"
        data-testid="order-summary"
      >
        <dt className="mt-12 pb-24 w-2/3 divider-1-light">Order Subtotal</dt>
        <dd
          className="mt-12 pb-24 w-1/3 text-right divider-1-light"
          data-testid="order-summary-subtotal"
        >
          {USDollarFormatter.format(cartSubtotal || subTotal)}
        </dd>

        {promoDiscount < 0 && (
          <span className="divider-1-light mt-12 pb-12 w-full flex flex-wrap">
            <Fragment>
              <dt className="my-12 w-2/3">
                Promo {promoCode} applied{message ? '*' : ''}
              </dt>
              <dd className="my-12 w-1/3 text-right">
                {USDollarFormatter.format(promoDiscount)}
              </dd>
            </Fragment>
          </span>
        )}

        {exchangeCredit < 0 && (
          <Fragment>
            <dt className="my-12 w-2/3">Exchange Credit Applied*</dt>
            <dd
              className="my-12 w-1/3 text-right"
              data-testid="order-summary-exchange-credit"
            >
              {USDollarFormatter.format(exchangeCredit)}
            </dd>
          </Fragment>
        )}

        <span className="divider-1-light mt-12 pb-12 w-full flex flex-wrap">
          {(!!roundUp || roundUpAmount > 0) && (
            <Fragment>
              <dt className="my-12 w-2/3">Round Up (Thank You!)</dt>
              <dd
                className="my-12 w-1/3 text-right"
                data-testid="order-summary-round-up-amount"
              >
                ${roundUp ? roundUp.toFixed(2) : roundUpAmount}
              </dd>
            </Fragment>
          )}

          <Fragment>
            <dt className="my-12 w-2/3">Convenience Fee</dt>
            <dd
              className="my-12 w-1/3 text-right"
              data-testid="order-summary-convenience-fee"
            >
              {isAnExchange
                ? USDollarFormatter.format(cartExchangeFee || exchangeFee)
                : USDollarFormatter.format(cartTransFee || transactionFee)}
            </dd>
          </Fragment>
        </span>

        <dt className="pt-12 my-12 w-2/3 font-bold">Order Total</dt>
        <dd
          className="pt-12 my-12 w-1/3 font-bold text-right"
          data-testid="order-summary-order-total"
        >
          {USDollarFormatter.format(total)}
        </dd>
      </dl>

      {isAnExchange && (
        <Fragment>
          <div className="text-14">
            *There will be no refunds for unused balance. Any remaining balance
            will be used to support our vision for a thriving aquatic world.
          </div>
          <button
            data-testid={'btnAbortExchange'}
            className="text-depths underline text-14"
            onClick={() => abortExchange()}
          >
            Return to original order.
          </button>
        </Fragment>
      )}
      {/* only need the promo code input box on Complete Your Purchase /new screen */}
      <PromoCode
        showPromoBox={showPromoBox}
        promoDiscount={promoDiscount}
        setPromoDiscount={setPromoDiscount}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        setNewTotal={setNewTotal}
        message={message}
        setMessage={setMessage}
        setRoundUpAmount={setRoundUpAmount}
        isAnExchange={isAnExchange}
      />

      {/* only need OrderActions component for Cart screen so if we can get the cart id from state, then we know we are in the React app */}
      {cart.id && (
        <div className="hidden md:block">
          <OrderActions />
        </div>
      )}
    </div>
  )
}

export default OrderSummary
